import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Sidebar, WorldList, CreateWorldWizard, SettingsModal, ImportWorldWizard, EmailVerificationBanner, CreditRequiredModal } from '../components';
import { useAuthStore, useWorldsStore } from '../store';
import { useSidebarStore } from '../store/ui';
import { useIsMobile } from '../hooks';
import { LandingPage } from './LandingPage';

type WizardIntent = 'companion' | 'narrative';

export function HomePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, isLoading: authLoading } = useAuthStore();
  const { worlds, isLoading: worldsLoading, fetchWorlds } = useWorldsStore();
  // /onboarding routes here with state.openWizard = 'companion' | 'narrative'
  // to deep-link the wizard into the right entry point. Read once on mount
  // so a refresh doesn't re-open it.
  const routedIntent = (location.state as { openWizard?: WizardIntent } | null)?.openWizard ?? null;
  const [wizardIntent, setWizardIntent] = useState<WizardIntent | null>(routedIntent);
  const [showCreateWizard, setShowCreateWizard] = useState(routedIntent !== null);
  const [showImportWizard, setShowImportWizard] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [importHandoff, setImportHandoff] = useState<{ title: string; prompt: string } | null>(null);
  const justRegistered = Boolean((location.state as { justRegistered?: boolean } | null)?.justRegistered);
  const [showCreditRequired, setShowCreditRequired] = useState(justRegistered);
  const onboardingRedirected = useRef(false);

  useEffect(() => {
    if (showCreditRequired || routedIntent) {
      // Clear the routing state so a refresh or remount doesn't replay it.
      navigate('/', { replace: true });
    }
  }, [showCreditRequired, routedIntent, navigate]);

  // First-time signup redirect. New users land on /onboarding to pick
  // their experience (roleplay vs. story) before hitting the dashboard.
  // Returning users with worlds skip this and see the dashboard directly.
  useEffect(() => {
    if (!isAuthenticated || authLoading || worldsLoading) return;
    if (onboardingRedirected.current) return;
    if (!justRegistered) return;
    if (showCreditRequired) return;
    if (worlds.length > 0) return;
    onboardingRedirected.current = true;
    navigate('/onboarding', { replace: true });
  }, [isAuthenticated, authLoading, worldsLoading, worlds.length, justRegistered, showCreditRequired, navigate]);

  const isMobile = useIsMobile();
  const toggleSidebar = useSidebarStore(state => state.toggleSidebar);

  useEffect(() => {
    if (isAuthenticated) {
      fetchWorlds();
    }
  }, [isAuthenticated, fetchWorlds]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-dark-900">
        <div className="text-center">
          <div className="spinner w-8 h-8 mx-auto mb-4" />
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LandingPage />;
  }

  return (
    <div className="flex h-screen bg-dark-900">
      <Sidebar
        worlds={worlds}
        onCreateWorld={() => setShowCreateWizard(true)}
        onImportWorld={() => setShowImportWizard(true)}
        onOpenSettings={() => setShowSettings(true)}
      />

      <main className="flex-1 overflow-auto flex flex-col">
        {/* Mobile header bar with hamburger */}
        {isMobile && (
          <div className="sticky top-0 z-30 bg-dark-800 border-b border-dark-500 px-4 py-3 flex items-center gap-3">
            <button
              onClick={toggleSidebar}
              className="text-dark-200 hover:text-dark-50 p-1"
              aria-label="Open menu"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <Link to="/" className="flex items-center gap-2">
              <img src="/vite.svg" alt="Textborne logo" className="h-5 w-5" />
              <span className="text-lg font-bold text-primary-400">Textborne</span>
            </Link>
          </div>
        )}

        <div className="flex-1 p-4 md:p-8">
          {worldsLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="spinner w-8 h-8" />
            </div>
          ) : (
            <div className="max-w-4xl mx-auto">
              <EmailVerificationBanner />
              {user?.is_admin && (
                <div className="mb-6 bg-dark-800 border border-primary-700/50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-lg font-semibold text-primary-200">Admin Access</h2>
                      <p className="text-sm text-dark-300">Quick links for administration tools</p>
                    </div>
                    <Link to="/admin" className="btn-secondary">Open Dashboard</Link>
                  </div>
                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <Link to="/admin/users" className="bg-dark-700 hover:bg-dark-600 rounded px-3 py-2 text-sm text-dark-200">
                      Manage Users
                    </Link>
                    <Link to="/admin/worlds" className="bg-dark-700 hover:bg-dark-600 rounded px-3 py-2 text-sm text-dark-200">
                      Inspect Worlds
                    </Link>
                    <Link to="/admin/invitations" className="bg-dark-700 hover:bg-dark-600 rounded px-3 py-2 text-sm text-dark-200">
                      Invitation Codes
                    </Link>
                  </div>
                </div>
              )}
              <div className="mb-8">
                <h1 className="text-3xl font-bold text-dark-50">
                  Welcome back{user?.display_name ? `, ${user.display_name}` : ''}!
                </h1>
                <p className="text-dark-200 mt-2">
                  Continue your stories or start a new narrative adventure.
                </p>
              </div>

              <WorldList
                worlds={worlds}
                onCreateNew={() => setShowCreateWizard(true)}
                onImportWorld={() => setShowImportWizard(true)}
              />

              {/* Decorative background image */}
              <div className="mt-12 flex justify-center pointer-events-none select-none" aria-hidden="true">
                <img
                  src="/textborne_logo_transparent.png"
                  alt=""
                  className="w-full max-w-3xl h-auto object-contain opacity-60"
                />
              </div>
            </div>
          )}
        </div>
      </main>

      <CreateWorldWizard
        isOpen={showCreateWizard}
        onClose={() => { setShowCreateWizard(false); setImportHandoff(null); setWizardIntent(null); }}
        initialData={importHandoff}
        lockedIntent={wizardIntent}
      />

      <ImportWorldWizard
        isOpen={showImportWizard}
        onClose={() => setShowImportWizard(false)}
        onHandoffToCreate={(data) => {
          setImportHandoff(data);
          setShowImportWizard(false);
          setShowCreateWizard(true);
        }}
      />

      <SettingsModal
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
      />

      <CreditRequiredModal
        isOpen={showCreditRequired}
        onClose={() => setShowCreditRequired(false)}
        context="signup"
      />
    </div>
  );
}
